<template>
  <div
    data-testid="fila-abrir"
    v-if="hasEditableQueues"
    class="cursor-pointer tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-transition hover:tw-bg-greyc-4"
  >
    <q-icon
      name="mdi-format-list-checks"
      size="18px"
      color="blue-grey-9"
    >
      <q-tooltip>Minhas filas</q-tooltip>
    </q-icon>

    <q-menu
      ref="queuesMenu"
      anchor="bottom middle"
      self="top middle"
      :offset="[0, 4]"
      @before-show="handleOpen"
      @show="focusSearch"
      @hide="handleClose"
    >
      <div class="queues-menu column no-wrap">
        <div class="row items-center justify-between q-px-sm q-pt-xs q-pb-none">
          <span class="text-caption text-grey-7">Minhas filas</span>
          <q-btn
            data-testid="fila-fechar"
            flat
            round
            dense
            size="xs"
            icon="mdi-close"
            v-close-popup
          />
        </div>

        <div
          v-if="loading"
          class="row justify-center q-pa-md"
        >
          <q-spinner
            color="primary"
            size="24px"
          />
        </div>

        <div
          v-else-if="isApplying"
          class="text-grey-6 q-pa-md text-center"
        >
          <q-icon
            name="mdi-progress-clock"
            size="sm"
          />
          <div class="q-mt-xs">
            Alteração em processamento
          </div>
          <div class="text-caption">
            O PBX está aplicando sua última alteração.
          </div>
        </div>

        <template v-else>
          <div class="q-px-sm q-pb-xs">
            <q-input
              data-testid="fila-busca"
              ref="searchInput"
              dense
              debounce="200"
              placeholder="Procurar"
              v-model="filter"
            >
              <template #append>
                <q-icon name="mdi-magnify" />
              </template>
            </q-input>
          </div>

          <template v-if="filteredQueues.length">
            <div class="q-px-sm q-py-xs">
              <q-checkbox
                data-testid="fila-selecionar-todas"
                keep-color
                size="xs"
                indeterminate-value
                :value="allEditableSelected"
                :color="hasSomeEditableSelected ? 'primary' : 'blue-grey-3'"
                @input="toggleSelectAllEditable"
                label="Selecionar todas"
              />
              <div class="text-caption text-grey-6">
                {{ selectionCounterLabel }}
              </div>
            </div>

            <q-separator />

            <div class="queues-list">
              <div
                v-for="queue in filteredQueues"
                :key="queue.id"
              >
                <q-checkbox
                  :data-testid="`fila-${queue.id}`"
                  size="xs"
                  color="primary"
                  :label="queue.name"
                  v-model="selection[queue.id]"
                  toggle-order="ft"
                />
              </div>
            </div>
          </template>

          <div
            v-else
            class="text-grey-6 q-pa-md text-center"
          >
            <template v-if="filter">
              <div>Nenhuma fila encontrada</div>
              <q-btn
                data-testid="fila-limpar-busca"
                flat
                dense
                size="xs"
                label="Limpar busca"
                @click="filter = ''"
              />
            </template>
            <template v-else>
              Nenhuma fila disponível
            </template>
          </div>

          <q-separator />

          <div class="row justify-end q-pa-xs">
            <q-btn
              data-testid="fila-confirmar"
              color="primary"
              label="OK"
              size="sm"
              :disable="disableApplyQueues"
              @click="handleApplyQueues"
            />
          </div>
        </template>
      </div>
    </q-menu>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import api from '@/libs/api'

export default {
  name: 'AgentQueuesButton',

  data () {
    return {
      filter: '',
      selection: {},
      loading: false,
      queueConfig: {
        canEditQueues: false,
        defaultQueueId: null,
        editableQueues: [],
        memberships: [],
        hasPendingSetting: false
      }
    }
  },

  computed: {
    ...mapState({
      agentId: ({ agentModule }) => agentModule?.session?.agent?.id,
      sessionCanEditQueues: ({ agentModule }) => agentModule?.session?.agent?.canEditQueues,
      sessionEditableQueues: ({ agentModule }) => agentModule?.session?.agent?.editableQueues,
      sessionDefaultQueueId: ({ agentModule }) => agentModule?.session?.agent?.defaultQueueId
    }),

    hasEditableQueues () {
      if (this.sessionCanEditQueues !== true) return false
      const editableQueues = this.sessionEditableQueues || []
      return editableQueues.some(queue => queue.id !== this.sessionDefaultQueueId)
    },

    isApplying () {
      return this.queueConfig.hasPendingSetting
    },

    shownEditable () {
      const { editableQueues, defaultQueueId } = this.queueConfig
      return editableQueues.filter(queue => queue.id !== defaultQueueId)
    },

    lockedKeptIds () {
      return this.queueConfig.memberships.filter(
        id => !this.shownEditable.some(queue => queue.id === id)
      )
    },

    filteredQueues () {
      if (!this.filter) return this.shownEditable
      const term = this.filter.toLowerCase()
      return this.shownEditable.filter(queue => queue.name.toLowerCase().includes(term))
    },

    finalQueues () {
      const selectedEditableIds = this.shownEditable
        .filter(queue => this.selection[queue.id])
        .map(queue => queue.id)
      return [...this.lockedKeptIds, ...selectedEditableIds]
    },

    allEditableSelected () {
      if (!this.filteredQueues.length) return false
      const selectedCount = this.filteredQueues.filter(queue => this.selection[queue.id]).length
      if (selectedCount === 0) return false
      if (selectedCount === this.filteredQueues.length) return true
      return null
    },

    hasSomeEditableSelected () {
      return this.filteredQueues.some(queue => this.selection[queue.id])
    },

    selectionCounterLabel () {
      const selectedFiltered = this.filteredQueues.filter(queue => this.selection[queue.id]).length
      if (this.filter) {
        return `${selectedFiltered} de ${this.filteredQueues.length} selecionadas`
      }
      const selectedShown = this.shownEditable.filter(queue => this.selection[queue.id]).length
      if (selectedShown === 0) return 'Nenhuma fila selecionada'
      if (selectedShown === this.shownEditable.length) return 'Todas as filas selecionadas'
      return `${selectedShown} filas selecionadas`
    },

    disableApplyQueues () {
      if (this.loading) return true
      if (!this.agentId) return true
      const sorted = arr => [...arr].sort()
      return (
        sorted(this.finalQueues).join(',') ===
        sorted(this.queueConfig.memberships).join(',')
      )
    }
  },

  methods: {
    resetSelection () {
      const newSelection = {}
      this.shownEditable.forEach(queue => {
        newSelection[queue.id] = this.queueConfig.memberships.includes(queue.id)
      })
      this.selection = newSelection
    },

    toggleSelectAllEditable () {
      const next = { ...this.selection }
      const shouldSelectAll = this.allEditableSelected !== true
      this.filteredQueues.forEach(queue => {
        next[queue.id] = shouldSelectAll
      })
      this.selection = next
    },

    focusSearch () {
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focus()
      })
    },

    async handleOpen () {
      if (!this.agentId) {
        this.$q.notify({ type: 'negative', message: 'Agente não identificado.' })
        return
      }

      this.loading = true
      try {
        const config = await api.getAgentQueueConfig(this.agentId)
        this.queueConfig = {
          canEditQueues: config?.canEditQueues ?? false,
          defaultQueueId: config?.defaultQueueId ?? null,
          editableQueues: config?.editableQueues ?? [],
          memberships: config?.memberships ?? [],
          hasPendingSetting: config?.hasPendingSetting ?? false
        }
        this.resetSelection()
      } catch (err) {
        console.error('[AgentQueuesButton] Erro ao carregar filas:', err)
        this.$q.notify({ type: 'negative', message: 'Não foi possível carregar as filas.' })
      } finally {
        this.loading = false
      }
    },

    handleClose () {
      this.filter = ''
      this.resetSelection()
    },

    async handleApplyQueues () {
      if (!this.agentId) {
        this.$q.notify({ type: 'negative', message: 'Agente não identificado.' })
        return
      }

      this.loading = true
      try {
        await api.patchAgentQueues(this.agentId, this.finalQueues)
        this.$q.notify({
          type: 'positive',
          message: 'Alteração enviada!',
          caption: 'O PBX está processando a alteração.'
        })
        this.$refs.queuesMenu.hide()
      } catch (err) {
        console.error('[AgentQueuesButton] Erro ao atualizar filas:', err)
        this.notifyPatchError(err)
      } finally {
        this.loading = false
      }
    },

    notifyPatchError (err) {
      const errors = err?.response?.data?.$errors
      const isPending = Array.isArray(errors) && errors.some(e => e.type === 'pending_settings')
      this.$q.notify({
        type: isPending ? 'warning' : 'negative',
        message: isPending
          ? 'Já existe uma alteração de filas pendente. Aguarde ela ser aplicada.'
          : 'Não foi possível atualizar as filas.'
      })
    }
  }
}
</script>

<style lang="sass" scoped>
.queues-menu
  width: 240px

.queues-list
  max-height: 200px
  overflow: auto
  padding: 0 8px
</style>
